import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';
import { RotateCw, RotateCcw, ZoomIn, ZoomOut, X, Check } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

/**
 * Erzeugt ein zugeschnittenes + rotiertes Bild als Blob
 */
const createCroppedImage = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const rotRad = (rotation * Math.PI) / 180;

  // Bounding Box nach Rotation berechnen
  const { width: bBoxWidth, height: bBoxHeight } = getRotatedSize(
    image.width,
    image.height,
    rotation
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Bild rotiert zeichnen
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // Zugeschnittene Pixel extrahieren
  const croppedData = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  );

  // Canvas auf Crop-Größe setzen
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(croppedData, 0, 0);

  // Als Blob zurückgeben
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Bildverarbeitung fehlgeschlagen'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = src;
  });

const getRotatedSize = (width, height, rotation) => {
  const rotRad = (rotation * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

/**
 * ImageCropModal
 * 
 * Props:
 * - imageSrc: string (Data-URL oder URL des Bildes)
 * - onComplete: (croppedBlob: Blob) => void
 * - onCancel: () => void
 * - aspectRatio: number (default 1, quadratisch/rund passend zum runden Avatar)
 * - cropShape: 'round' | 'rect' (default 'round')
 */
const ImageCropModal = ({ imageSrc, onComplete, onCancel, aspectRatio = 1, cropShape = 'round' }) => {
  useBodyScrollLock(true);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels, rotation);
      onComplete(croppedBlob);
    } catch (err) {
      console.error('Fehler beim Zuschneiden:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const rotateLeft = () => setRotation((r) => (r - 90) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !isProcessing) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel, isProcessing]);

  const modal = (
    <div
      // Bewusst dunkle Huelle in BEIDEN Themes (dokumentierte Ausnahme, wie
      // Modal-Backdrops): ein Bildeditor braucht neutrale, dunkle Umgebung,
      // damit das Foto selbst richtig beurteilt werden kann.
      className="fixed inset-0 z-[9999] flex flex-col bg-gray-900/95 backdrop-blur-sm"
      // Safe-Area des iPhones: Dieses Overlay haengt per Portal direkt am <body>
      // und reicht bis an alle vier Bildschirmkanten. Ohne diese Polsterung lag
      // die Kopfzeile unter Statusleiste/Dynamic Island — "Fertig" war in der
      // nativen App nicht antippbar, das Profil-Setup liess sich nicht
      // abschliessen. Gleiche Formel wie WelcomeTour.js (dort erprobt):
      // Web = env() = 0, native App = mindestens --native-safe-area-top, auch
      // wenn WebKit env() kurz nach dem Start noch mit 0 meldet.
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), var(--native-safe-area-top, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Bild zuschneiden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white/10 backdrop-blur-md border-b border-white/10">
        <button
          onClick={onCancel}
          className="shrink-0 min-h-[44px] flex items-center gap-1.5 px-3 py-2 rounded-xl text-white/80 hover:text-white hover:bg-white/10 transition-all text-sm font-medium"
        >
          <X className="h-4 w-4" />
          Abbrechen
        </button>
        <h3 className="min-w-0 truncate px-2 text-white font-semibold text-sm">Bild anpassen</h3>
        <button
          onClick={handleConfirm}
          disabled={isProcessing}
          className="shrink-0 min-h-[44px] flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary-hover transition-all text-sm font-medium disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
          {isProcessing ? 'Moment…' : 'Fertig'}
        </button>
      </div>

      {/* Crop Area */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspectRatio}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          // Auf ganze Grad runden: Beim Drehen per Geste liefert der Cropper
          // Fliesskommawerte (z.B. 12.34567890123°), die ungerundet als
          // Zahlenkolonne in der Anzeige landeten.
          onRotationChange={(value) => setRotation(Math.round(value))}
          onCropComplete={onCropComplete}
          cropShape={cropShape}
          showGrid={cropShape !== 'round'}
          style={{
            containerStyle: { background: '#111' },
            cropAreaStyle: { border: '2px solid rgba(168, 85, 247, 0.7)' },
          }}
        />
      </div>

      {/* Controls */}
      <div className="px-4 py-4 bg-white/10 backdrop-blur-md border-t border-white/10 space-y-3">
        {/* Rotation */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={rotateLeft}
            className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all active:scale-95"
            title="90° links drehen"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
          <span className="text-white/60 text-xs font-medium min-w-[50px] text-center">
            {Math.round(rotation)}°
          </span>
          <button
            onClick={rotateRight}
            className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all active:scale-95"
            title="90° rechts drehen"
          >
            <RotateCw className="h-5 w-5" />
          </button>
        </div>

        {/* Zoom Slider */}
        <div className="flex items-center gap-3">
          <ZoomOut className="h-4 w-4 text-white/50 flex-shrink-0" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-5
              [&::-webkit-slider-thumb]:h-5
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-primary
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:w-5
              [&::-moz-range-thumb]:h-5
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-primary
              [&::-moz-range-thumb]:border-0
              [&::-moz-range-thumb]:cursor-pointer"
          />
          <ZoomIn className="h-4 w-4 text-white/50 flex-shrink-0" />
        </div>

        {/* Fine Rotation Slider */}
        <div className="flex items-center gap-3">
          <RotateCcw className="h-3.5 w-3.5 text-white/40 flex-shrink-0" />
          <input
            type="range"
            min={-45}
            max={45}
            step={1}
            value={rotation % 90 === 0 ? 0 : rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            className="flex-1 h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-white/60
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-white/60
              [&::-moz-range-thumb]:border-0
              [&::-moz-range-thumb]:cursor-pointer"
          />
          <RotateCw className="h-3.5 w-3.5 text-white/40 flex-shrink-0" />
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ImageCropModal;
